/**
 * StackEditPage — two modes based on deployment status:
 *
 *  • draft / failed  → Full reconfigure wizard (all infra fields editable, nothing deployed yet)
 *  • any deployed status → Restricted form (name + description only; infra fields locked)
 *
 * Why infra fields are locked after deployment:
 *   Changing cloud, region, or template for a running system isn't an edit — it's a migration.
 *   PVCs are zone-pinned, StatefulSets can't move between clusters, template changes recreate
 *   resources and destroy database volumes. The correct path is: create a new system → migrate
 *   data → cutover DNS → delete old system.
 */
import React, { useEffect, useState } from "react";

import { Alert, Box, Button, CircularProgress, Container } from "@mui/material";
import axios from "axios";
import { useParams } from "react-router";

import { ApplicationEndpoints } from "@generated/application";
import { apiBaseUrls } from "src/platform/constants/shared-constants";
import { useAppNavigation } from "src/platform/hooks/useAppNavigation";

import RestrictedEditForm from "./RestrictedEditForm";
import type { ExistingSystem } from "./types";
import { authHeader, isDeployed } from "./wizard-helpers";
import WizardEditPage from "./WizardEditPage";

export default function StackEditPage() {
  const { orgAlias, projectAlias, sysAlias } = useParams<{
    orgAlias: string;
    projectAlias: string;
    sysAlias: string;
  }>();
  const nav = useAppNavigation();

  const [system, setSystem] = useState<ExistingSystem | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);

  useEffect(() => {
    if (!sysAlias) return;
    const url = `${apiBaseUrls.BACKEND}${ApplicationEndpoints.GET_APPLICATION.replace(":id", sysAlias)}`;
    axios
      .get<ExistingSystem>(url, { headers: authHeader() })
      .then((r) => setSystem(r.data))
      .catch((err: unknown) => {
        const msg = axios.isAxiosError(err)
          ? ((err.response?.data as { message?: string })?.message ??
            err.message)
          : "Failed to load stack.";
        setLoadError(msg);
      });
  }, [sysAlias]);

  if (loadError) {
    return (
      <Container maxWidth="sm" sx={{ py: 4 }}>
        <Alert severity="error">{loadError}</Alert>
        <Button sx={{ mt: 2 }} onClick={() => window.history.back()}>
          Go back
        </Button>
      </Container>
    );
  }

  if (!system) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", p: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (isDeployed(system.status)) {
    return (
      <RestrictedEditForm
        system={system}
        orgAlias={orgAlias!}
        projectAlias={projectAlias!}
        sysAlias={sysAlias!}
        onDone={() => nav.goToStackDetails(orgAlias!, projectAlias!, sysAlias!)}
      />
    );
  }

  return (
    <WizardEditPage
      system={system}
      orgAlias={orgAlias!}
      projectAlias={projectAlias!}
      sysAlias={sysAlias!}
      onDone={() => nav.goToStackDetails(orgAlias!, projectAlias!, sysAlias!)}
    />
  );
}
